import React, { useState, useEffect, useRef, CSSProperties, RefObject } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { trackAddToCart, trackPromoCta, trackPromoView } from "../utils/metaPixel";

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Reusable icons (inline SVG) ─────────────────────────────

const StarIcon: React.FC = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="#f59e0b" stroke="none">
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const CheckIcon: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const BookIcon: React.FC = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
    <path d="M4 4.5A2.5 2.5 0 016.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15z" />
  </svg>
);

const WandIcon: React.FC = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M15 4V2m0 2v2m0-2h2m-2 0h-2" />
    <path d="M8.5 8.5L3 14l7 7 5.5-5.5" />
    <path d="M9.5 7.5l7 7" />
  </svg>
);

const TruckIcon: React.FC = () => (
  <svg
    width="32"
    height="32"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#7c3aed"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect x="1" y="3" width="15" height="13" rx="1" />
    <path d="M16 8h4l3 5v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" />
    <circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
);

interface GiftIconProps {
  size?: number;
}

const GiftIcon: React.FC<GiftIconProps> = ({ size = 24 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 12 20 22 4 22 4 12" />
    <rect x="2" y="7" width="20" height="5" />
    <line x1="12" y1="22" x2="12" y2="7" />
    <path d="M12 7H7.5a2.5 2.5 0 010-5C11 2 12 7 12 7z" />
    <path d="M12 7h4.5a2.5 2.5 0 000-5C13 2 12 7 12 7z" />
  </svg>
);

const ChevronDown: React.FC = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="6 9 12 15 18 9" />
  </svg>
);

// ── Intersection Observer hook ──────────────────────────────

function useInView(threshold: number = 0.15): [RefObject<HTMLElement | null>, boolean] {
  const ref = useRef<HTMLElement | null>(null);
  const [visible, setVisible] = useState<boolean>(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVisible(true);
          obs.disconnect();
        }
      },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);

  return [ref, visible];
}

// ── FAQ Accordion Item ──────────────────────────────────────

interface FaqItemProps {
  q: string;
  a: string;
}

const FaqItem: React.FC<FaqItemProps> = ({ q, a }) => {
  const [open, setOpen] = useState<boolean>(false);

  return (
    <div
      onClick={() => setOpen(!open)}
      style={{
        borderBottom: "1px solid #e9e5f5",
        padding: "20px 0",
        cursor: "pointer",
        userSelect: "none",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontWeight: 600,
            fontSize: 16,
            color: "#1e1b4b",
          }}
        >
          {q}
        </span>
        <span
          style={{
            transform: open ? "rotate(180deg)" : "rotate(0)",
            transition: "transform 0.3s",
            flexShrink: 0,
          }}
        >
          <ChevronDown />
        </span>
      </div>
      <div
        style={{
          maxHeight: open ? 200 : 0,
          overflow: "hidden",
          transition: "max-height 0.35s ease",
        }}
      >
        <p
          style={{
            margin: "12px 0 0",
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 15,
            color: "#6b7280",
            lineHeight: 1.6,
          }}
        >
          {a}
        </p>
      </div>
    </div>
  );
};

// ── Types ───────────────────────────────────────────────────

interface HowItWorksStep {
  icon: React.ReactNode;
  step: string;
  num: string;
}

interface BookCard {
  key: string;
  gradient: string;
  emoji: string;
}

interface SparklePosition {
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  delay: string;
}

interface Review {
  _id: string;
  userName: string;
  text: string;
  rating: number;
  createdAt: string;
}

interface Book {
  id: string;
  title: string;
  description: string;
  price: number;
  currency: string;
  imageUrl: string;
  allowedCountries?: string[];
}

const COUNTRY_CONFIG: Record<string, { language: string; countryName: string }> = {
  ro: { language: "ro", countryName: "Romania" },
  de: { language: "de", countryName: "Germany" },
  fr: { language: "fr", countryName: "France" },
  en: { language: "en", countryName: "United Kingdom" },
  // add more as needed
};

// ── MAIN COMPONENT ──────────────────────────────────────────

const LandingPage: React.FC = () => {
  const [heroRef, heroVisible] = useInView(0.1);
  const [howRef, howVisible] = useInView();
  const [booksRef, booksVisible] = useInView();
  const [reviewsRef, reviewsVisible] = useInView();
  const [giftRef, giftVisible] = useInView();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewsLoading, setReviewsLoading] = useState<boolean>(true);

  const [featuredBooks, setFeaturedBooks] = useState<Book[]>([]);
  const [booksLoading, setBooksLoading] = useState<boolean>(true);

  const { country } = useParams<{ country: string }>();
  const { t, i18n } = useTranslation();

  const config = COUNTRY_CONFIG[country || "ro"] || COUNTRY_CONFIG["ro"];

  const ctaUrl: string = "/library"; // ← change to your create/browse route

  // CTA click handler
  const handleCtaClick = (ctaLocation: string) => {
    trackPromoCta(ctaLocation);
  };

    // Book click handler
  const handleBookClick = (bookId: string, bookTitle: string, price: number) => {
    trackAddToCart(bookTitle, price);
  };

//   const API_URL = import.meta.env.VITE_API_URL || ""; // ← adjust to match your env setup

  useEffect(() => {
    trackPromoView(country || "ro");
    const fetchReviews = async () => {
      try {
        const response = await fetch(`${API_URL}/reviews?limit=3`);
        if (!response.ok) throw new Error("Failed to fetch reviews");
        const data: Review[] = await response.json();
        setReviews(data);
      } catch (error) {
        console.error("Error fetching reviews:", error);
      } finally {
        setReviewsLoading(false);
      }
    };
    fetchReviews();

      const fetchBooks = async () => {
          try {
              const response = await fetch(`${API_URL}/books`);
              if (!response.ok) throw new Error("Failed to fetch books");
              const books = await response.json();
            //   setFeaturedBooks(
            //       books.map((book: any) => ({
            //           id: book._id || book.id,
            //           title: book.title,
            //           description: book.description || "",
            //           price: book.price,
            //           currency: book.currency,
            //           imageUrl: book.imageUrl,
            //           allowedCountries: book.allowedCountries,
            //       }))
            //   );
              setFeaturedBooks(
                  books
                      .filter((book: any) =>
                          !book.allowedCountries ||
                          book.allowedCountries.includes(config.countryName)
                      )
                      .map((book: any) => ({
                          id: book._id || book.id,
                          title: book.title,
                          description: book.description || "",
                          price: book.price,
                          currency: book.currency,
                          imageUrl: book.imageUrl,
                          allowedCountries: book.allowedCountries,
                      }))
              );
          } catch (error) {
              console.error("Error fetching featured books:", error);
          } finally {
              setBooksLoading(false);
          }
      };
      fetchBooks();

      i18n.changeLanguage(config.language);
      localStorage.setItem('preferredLanguage', config.language);
  }, [config.language, i18n]);

  const fadeUp = (visible: boolean, delay: number = 0): CSSProperties => ({
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(28px)",
    transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
    });

    // Option A: Buy a ready-made book
    const pathBuySteps: HowItWorksStep[] = [
        { icon: <BookIcon />, step: "pathBuy.step1", num: "1" },
        { icon: <WandIcon />, step: "pathBuy.step2", num: "2" },
        { icon: <TruckIcon />, step: "pathBuy.step3", num: "3" },
    ];

    // Option B: Create your own book
    const pathCreateSteps: HowItWorksStep[] = [
        { icon: <WandIcon />, step: "pathCreate.step1", num: "1" },
        { icon: <BookIcon />, step: "pathCreate.step2", num: "2" },
        { icon: <TruckIcon />, step: "pathCreate.step3", num: "3" },
    ];

    const [howRef2, howVisible2] = useInView();

  const bookCards: BookCard[] = [
    {
      key: "book1",
      gradient: "linear-gradient(135deg, #ede9fe 0%, #ddd6fe 100%)",
      emoji: "📖",
    },
    {
      key: "book2",
      gradient: "linear-gradient(135deg, #fff7ed 0%, #fed7aa 100%)",
      emoji: "🌙",
    },
  ];

  const sparkles: SparklePosition[] = [
    { top: "12%", left: "8%", delay: "0s" },
    { top: "20%", right: "12%", delay: "0.8s" },
    { bottom: "30%", left: "15%", delay: "1.6s" },
  ];

  return (
    <div style={{ background: "#fffbfe", minHeight: "100vh", overflowX: "hidden" }}>
      {/* DM Sans and Playfair Display are self-hosted — see src/index.css */}

      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-10px)} }
        @keyframes sparkle { 0%,100%{opacity:0.3;transform:scale(0.8)} 50%{opacity:1;transform:scale(1.2)} }
        .sp-landing-cta {
          display: inline-flex; align-items: center; gap: 8px;
          background: linear-gradient(135deg, #7c3aed, #6d28d9);
          color: #fff; border: none; border-radius: 50px;
          padding: 16px 36px; font-size: 17px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; text-decoration: none;
          box-shadow: 0 4px 20px rgba(124,58,237,0.35);
          transition: transform 0.2s, box-shadow 0.2s;
        }
        .sp-landing-cta:hover { transform: translateY(-2px); box-shadow: 0 6px 28px rgba(124,58,237,0.45); }
        .sp-landing-cta-outline {
          display: inline-flex; align-items: center; gap: 8px;
          background: transparent; color: #7c3aed;
          border: 2px solid #7c3aed; border-radius: 50px;
          padding: 14px 32px; font-size: 16px; font-weight: 600;
          font-family: 'DM Sans', sans-serif;
          cursor: pointer; text-decoration: none;
          transition: background 0.2s, color 0.2s;
        }
        .sp-landing-cta-outline:hover { background: #7c3aed; color: #fff; }
        .sp-book-card {
          background: #fff; border-radius: 20px;
          box-shadow: 0 2px 24px rgba(124,58,237,0.08);
          overflow: hidden; transition: transform 0.3s, box-shadow 0.3s;
        }
        .sp-book-card:hover { transform: translateY(-4px); box-shadow: 0 8px 32px rgba(124,58,237,0.15); }
      `}</style>

      {/* ══════════════════════════════════════════════════════
           HERO
         ══════════════════════════════════════════════════════ */}
      <section
        ref={heroRef as RefObject<HTMLElement>}
        style={{
          position: "relative",
          padding: "60px 24px 50px",
          textAlign: "center",
          overflow: "hidden",
          background:
            "linear-gradient(175deg, #f5f0ff 0%, #fffbfe 60%, #fff7ed 100%)",
        }}
      >
        {/* Decorative blobs */}
        <div
          style={{
            position: "absolute",
            top: -60,
            right: -60,
            width: 200,
            height: 200,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(124,58,237,0.08), transparent 70%)",
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            position: "absolute",
            bottom: -40,
            left: -40,
            width: 160,
            height: 160,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(251,146,60,0.1), transparent 70%)",
            pointerEvents: "none",
          }}
        />

        {/* Sparkles */}
        {sparkles.map((pos, i) => {
          const { delay, ...posStyles } = pos;
          return (
            <div
              key={i}
              style={{
                position: "absolute",
                ...posStyles,
                width: 8,
                height: 8,
                borderRadius: "50%",
                background: "#f59e0b",
                animation: "sparkle 2.5s ease-in-out infinite",
                animationDelay: delay,
                pointerEvents: "none",
              }}
            />
          );
        })}

        <div style={fadeUp(heroVisible, 0)}>
          <span
            style={{
              display: "inline-block",
              background: "#ede9fe",
              color: "#6d28d9",
              fontFamily: "'DM Sans', sans-serif",
              fontWeight: 600,
              fontSize: 13,
              padding: "6px 16px",
              borderRadius: 50,
              letterSpacing: 0.5,
              marginBottom: 20,
            }}
          >
            {t("landing.hero.badge")}
          </span>
        </div>

        <h1
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: "clamp(36px, 8vw, 56px)",
            lineHeight: 1.15,
            color: "#1e1b4b",
            marginBottom: 20,
            ...fadeUp(heroVisible, 0.1),
          }}
        >
          {t("landing.hero.title1")}
          <br />
          <span style={{ color: "#7c3aed", fontStyle: "italic" }}>
            {t("landing.hero.title2")}
          </span>
        </h1>

        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 17,
            color: "#6b7280",
            lineHeight: 1.65,
            maxWidth: 480,
            margin: "0 auto 32px",
            ...fadeUp(heroVisible, 0.2),
          }}
        >
          {t("landing.hero.subtitle")}
        </p>

        <div style={fadeUp(heroVisible, 0.3)}>
          <a href={ctaUrl} className="sp-landing-cta" onClick={() => handleCtaClick("hero")}>
            {t("landing.hero.cta")} →
          </a>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 13,
              color: "#a1a1aa",
              marginTop: 10,
            }}
          >
            {t("landing.hero.ctaSub")}
          </p>
        </div>

        {/* Trust badges */}
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 16,
            marginTop: 36,
            ...fadeUp(heroVisible, 0.4),
          }}
        >
          {(["trust1", "trust2", "trust3"] as const).map((k) => (
            <div
              key={k}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontFamily: "'DM Sans', sans-serif",
                fontSize: 13,
                color: "#6d28d9",
                fontWeight: 500,
              }}
            >
              <CheckIcon />
              {t(`landing.hero.${k}`)}
            </div>
          ))}
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
           HOW IT WORKS
         ══════════════════════════════════════════════════════ */}
      {/* ══════════════════════════════════════════════════════
     HOW IT WORKS — PATH A: Buy a ready-made book
   ══════════════════════════════════════════════════════ */}
          <section
              ref={howRef as RefObject<HTMLElement>}
              style={{ padding: "64px 24px 32px", maxWidth: 600, margin: "0 auto" }}
          >
              <h2
                  style={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: 28,
                      color: "#1e1b4b",
                      textAlign: "center",
                      marginBottom: 12,
                      ...fadeUp(howVisible),
                  }}
              >
                  {t("landing.howItWorks.title")}
              </h2>
              <p
                  style={{
                      fontFamily: "'DM Sans', sans-serif",
                      fontSize: 15,
                      color: "#6b7280",
                      textAlign: "center",
                      marginBottom: 40,
                      lineHeight: 1.5,
                      ...fadeUp(howVisible, 0.05),
                  }}
              >
                  {t("landing.howItWorks.subtitle")}
              </p>

              {/* Path A label */}
              <div
                  style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#ede9fe",
                      color: "#6d28d9",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      padding: "8px 20px",
                      borderRadius: 50,
                      marginBottom: 28,
                      ...fadeUp(howVisible, 0.1),
                  }}
              >
                  {t("landing.howItWorks.pathBuy.label")}
              </div>

              {pathBuySteps.map(({ icon, step, num }, i) => (
                  <div
                      key={step}
                      style={{
                          display: "flex",
                          gap: 20,
                          marginBottom: 36,
                          alignItems: "flex-start",
                          ...fadeUp(howVisible, 0.15 + 0.1 * i),
                      }}
                  >
                      <div
                          style={{
                              width: 64,
                              height: 64,
                              borderRadius: 16,
                              background: "#f5f0ff",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              position: "relative",
                          }}
                      >
                          {icon}
                          <span
                              style={{
                                  position: "absolute",
                                  top: -6,
                                  right: -6,
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  background: "#7c3aed",
                                  color: "#fff",
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                              }}
                          >
                              {num}
                          </span>
                      </div>
                      <div>
                          <h3
                              style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 700,
                                  fontSize: 17,
                                  color: "#1e1b4b",
                                  marginBottom: 4,
                              }}
                          >
                              {t(`landing.howItWorks.${step}.title`)}
                          </h3>
                          <p
                              style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: 15,
                                  color: "#6b7280",
                                  lineHeight: 1.55,
                              }}
                          >
                              {t(`landing.howItWorks.${step}.desc`)}
                          </p>
                      </div>
                  </div>
              ))}
          </section>

{/* ══════════════════════════════════════════════════════
     HOW IT WORKS — PATH B: Create your own book
   ══════════════════════════════════════════════════════ */}
          <section
              ref={howRef2 as RefObject<HTMLElement>}
              style={{
                  padding: "32px 24px 64px",
                  maxWidth: 600,
                  margin: "0 auto",
                  borderTop: "1px solid #e9e5f5",
              }}
          >
              {/* Path B label */}
              <div
                  style={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 8,
                      background: "#fff7ed",
                      color: "#c20cb6",
                      fontFamily: "'DM Sans', sans-serif",
                      fontWeight: 600,
                      fontSize: 14,
                      padding: "8px 20px",
                      borderRadius: 50,
                      marginBottom: 28,
                      ...fadeUp(howVisible2, 0.1),
                  }}
              >
                  {t("landing.howItWorks.pathCreate.label")}
              </div>

              {pathCreateSteps.map(({ icon, step, num }, i) => (
                  <div
                      key={step}
                      style={{
                          display: "flex",
                          gap: 20,
                          marginBottom: 36,
                          alignItems: "flex-start",
                          ...fadeUp(howVisible2, 0.15 + 0.1 * i),
                      }}
                  >
                      <div
                          style={{
                              width: 64,
                              height: 64,
                              borderRadius: 16,
                              background: "#fff7ed",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                              position: "relative",
                          }}
                      >
                          {icon}
                          <span
                              style={{
                                  position: "absolute",
                                  top: -6,
                                  right: -6,
                                  width: 22,
                                  height: 22,
                                  borderRadius: "50%",
                                  background: "#c20cb6",
                                  color: "#fff",
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                              }}
                          >
                              {num}
                          </span>
                      </div>
                      <div>
                          <h3
                              style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontWeight: 700,
                                  fontSize: 17,
                                  color: "#1e1b4b",
                                  marginBottom: 4,
                              }}
                          >
                              {t(`landing.howItWorks.${step}.title`)}
                          </h3>
                          <p
                              style={{
                                  fontFamily: "'DM Sans', sans-serif",
                                  fontSize: 15,
                                  color: "#6b7280",
                                  lineHeight: 1.55,
                              }}
                          >
                              {t(`landing.howItWorks.${step}.desc`)}
                          </p>
                      </div>
                  </div>
              ))}
          </section>

      {/* ══════════════════════════════════════════════════════
           BOOKS SHOWCASE
         ══════════════════════════════════════════════════════ */}
      <section
        ref={booksRef as RefObject<HTMLElement>}
        style={{
          padding: "48px 24px 64px",
          background: "linear-gradient(180deg, #fff 0%, #f5f0ff 100%)",
        }}
      >
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 28,
              color: "#1e1b4b",
              marginBottom: 8,
              ...fadeUp(booksVisible),
            }}
          >
            {t("landing.books.title")}
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              color: "#6b7280",
              marginBottom: 40,
              lineHeight: 1.5,
              ...fadeUp(booksVisible, 0.1),
            }}
          >
            {/* {t("landing.books.subtitle")} */}
          </p>

                  {/* Book Cards */}
                  {/* {booksLoading ? (
                      <div style={{ textAlign: "center", padding: "40px 0" }}>
                          <div
                              style={{
                                  width: 32,
                                  height: 32,
                                  border: "3px solid #e9e5f5",
                                  borderTopColor: "#7c3aed",
                                  borderRadius: "50%",
                                  animation: "spin 0.8s linear infinite",
                                  margin: "0 auto",
                              }}
                          />
                      </div>
                  ) : (
                      <>
                          {featuredBooks.map((book, i) => (
                              <div
                                  key={book.id}
                                  className="sp-book-card"
                                  style={{
                                      marginBottom: 24,
                                      textAlign: "left" as const,
                                      ...fadeUp(booksVisible, 0.15 * (i + 1)),
                                  }}
                              >
                                  <div
                                      style={{
                                          height: 220,
                                          background: "linear-gradient(135deg, #f5f0ff 0%, #ede9fe 100%)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                      }}
                                  >
                                      <img
                                          src={book.imageUrl}
                                          alt={book.title}
                                          style={{
                                              height: "100%",
                                              maxWidth: "100%",
                                              objectFit: "contain",
                                          }}
                                      />
                                  </div>
                                  <div style={{ padding: 24 }}>
                                      <h3
                                          style={{
                                              fontFamily: "'Playfair Display', serif",
                                              fontWeight: 700,
                                              fontSize: 20,
                                              color: "#1e1b4b",
                                              marginBottom: 8,
                                          }}
                                      >
                                          {book.title}
                                      </h3>
                                      <p
                                          style={{
                                              fontFamily: "'DM Sans', sans-serif",
                                              fontSize: 15,
                                              color: "#6b7280",
                                              lineHeight: 1.55,
                                              marginBottom: 12,
                                          }}
                                      >
                                          {book.description}
                                      </p>
                                      <p
                                          style={{
                                              fontFamily: "'DM Sans', sans-serif",
                                              fontSize: 18,
                                              fontWeight: 700,
                                              color: "#7c3aed",
                                              marginBottom: 20,
                                          }}
                                      >
                                          {book.currency} {book.price.toFixed(2)}
                                      </p>
                                      <a
                                          href={`/book/${book.id}`}
                                          className="sp-landing-cta-outline"
                                          style={{ fontSize: 14, padding: "10px 24px" }}
                                      >
                                          {t("landing.books.cta")} →
                                      </a>
                                  </div>
                              </div>
                          ))}
                      </>
                  )} */}

                  {booksLoading ? (
  <div style={{ textAlign: "center", padding: "40px 0" }}>
    <div
      style={{
        width: 32,
        height: 32,
        border: "3px solid #e9e5f5",
        borderTopColor: "#7c3aed",
        borderRadius: "50%",
        animation: "spin 0.8s linear infinite",
        margin: "0 auto",
      }}
    />
  </div>
) : featuredBooks.length === 0 ? (
  <div style={{ textAlign: "center", padding: "40px 20px" }}>
    <p
      style={{
        fontFamily: "'DM Sans', sans-serif",
        fontSize: 16,
        color: "#6b7280",
        lineHeight: 1.6,
        marginBottom: 24,
      }}
    >
      {t("landing.books.empty")}
    </p>
    <a href={ctaUrl} className="sp-landing-cta" onClick={() => handleCtaClick("gift")}>
      {t("landing.hero.cta")} →
    </a>
  </div>
) : (
  <>
    {featuredBooks.map((book, i) => (
      
                              <div
                                  key={book.id}
                                  className="sp-book-card"
                                  style={{
                                      marginBottom: 24,
                                      textAlign: "left" as const,
                                      ...fadeUp(booksVisible, 0.15 * (i + 1)),
                                  }}
                              >
                                  <div
                                      style={{
                                          height: 220,
                                          background: "linear-gradient(135deg, #f5f0ff 0%, #ede9fe 100%)",
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          overflow: "hidden",
                                      }}
                                  >
                                      <img
                                          src={book.imageUrl}
                                          alt={book.title}
                                          style={{
                                              height: "100%",
                                              maxWidth: "100%",
                                              objectFit: "contain",
                                          }}
                                      />
                                  </div>
                                  <div style={{ padding: 24 }}>
                                      <h3
                                          style={{
                                              fontFamily: "'Playfair Display', serif",
                                              fontWeight: 700,
                                              fontSize: 20,
                                              color: "#1e1b4b",
                                              marginBottom: 8,
                                          }}
                                      >
                                          {book.title}
                                      </h3>
                                      <p
                                          style={{
                                              fontFamily: "'DM Sans', sans-serif",
                                              fontSize: 15,
                                              color: "#6b7280",
                                              lineHeight: 1.55,
                                              marginBottom: 12,
                                          }}
                                      >
                                          {book.description}
                                      </p>
                                      <p
                                          style={{
                                              fontFamily: "'DM Sans', sans-serif",
                                              fontSize: 18,
                                              fontWeight: 700,
                                              color: "#7c3aed",
                                              marginBottom: 20,
                                          }}
                                      >
                                          {book.currency} {book.price.toFixed(2)}
                                      </p>
                                      <a
                                          href={"/cart?bookId=" + book.id}
                                          className="sp-landing-cta-outline"
                                          style={{ fontSize: 14, padding: "10px 24px" }}
                                          onClick={() => handleBookClick(book.id, book.title, book.price)}
                                      >
                                          {t("landing.books.cta")} →
                                      </a>
                                  </div>
                              </div>
    ))}
  </>
)}
          
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
           REVIEWS
         ══════════════════════════════════════════════════════ */}
      <section
        ref={reviewsRef as RefObject<HTMLElement>}
        style={{ padding: "64px 24px", maxWidth: 600, margin: "0 auto" }}
      >
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: 28,
            color: "#1e1b4b",
            textAlign: "center",
            marginBottom: 40,
            ...fadeUp(reviewsVisible),
          }}
        >
          {t("landing.reviews.title")}
        </h2>

        {reviewsLoading ? (
          <div style={{ textAlign: "center", padding: "40px 0" }}>
            <div
              style={{
                width: 32,
                height: 32,
                border: "3px solid #e9e5f5",
                borderTopColor: "#7c3aed",
                borderRadius: "50%",
                animation: "spin 0.8s linear infinite",
                margin: "0 auto",
              }}
            />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </div>
        ) : reviews.length === 0 ? null : (
          reviews.map((review, i) => (
            <div
              key={review._id}
              style={{
                background: "#fff",
                borderRadius: 16,
                padding: 24,
                marginBottom: 20,
                boxShadow: "0 1px 12px rgba(124,58,237,0.06)",
                borderLeft: "4px solid #7c3aed",
                ...fadeUp(reviewsVisible, 0.1 * (i + 1)),
              }}
            >
              <div style={{ display: "flex", gap: 2, marginBottom: 10 }}>
                {Array.from({ length: 5 }, (_, s) => (
                  <svg
                    key={s}
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill={s < review.rating ? "#f59e0b" : "#e5e7eb"}
                    stroke="none"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                ))}
              </div>
              <p
                style={{
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 15,
                  color: "#374151",
                  lineHeight: 1.6,
                  fontStyle: "italic",
                  marginBottom: 14,
                }}
              >
                &ldquo;{review.text}&rdquo;
              </p>
              <div>
                <span
                  style={{
                    fontFamily: "'DM Sans', sans-serif",
                    fontWeight: 700,
                    fontSize: 14,
                    color: "#1e1b4b",
                  }}
                >
                  {review.userName}
                </span>
              </div>
            </div>
          ))
        )}
      </section>

      {/* ══════════════════════════════════════════════════════
           PERFECT GIFT
         ══════════════════════════════════════════════════════ */}
      <section
        ref={giftRef as RefObject<HTMLElement>}
        style={{
          padding: "64px 24px",
          background: "linear-gradient(180deg, #fffbfe 0%, #f5f0ff 100%)",
        }}
      >
        <div style={{ maxWidth: 600, margin: "0 auto", textAlign: "center" }}>
          <div
            style={{
              color: "#7c3aed",
              marginBottom: 16,
              ...fadeUp(giftVisible),
            }}
          >
            <GiftIcon size={40} />
          </div>
          <h2
            style={{
              fontFamily: "'Playfair Display', serif",
              fontWeight: 700,
              fontSize: 28,
              color: "#1e1b4b",
              marginBottom: 12,
              ...fadeUp(giftVisible, 0.1),
            }}
          >
            {t("landing.gift.title")}
          </h2>
          <p
            style={{
              fontFamily: "'DM Sans', sans-serif",
              fontSize: 15,
              color: "#6b7280",
              lineHeight: 1.6,
              marginBottom: 32,
              ...fadeUp(giftVisible, 0.15),
            }}
          >
            {t("landing.gift.subtitle")}
          </p>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 12,
              marginBottom: 36,
              ...fadeUp(giftVisible, 0.2),
            }}
          >
            {(["item1", "item2", "item3", "item4"] as const).map((k) => (
              <span
                key={k}
                style={{
                  background: "#fff",
                  border: "1px solid #e9e5f5",
                  borderRadius: 50,
                  padding: "8px 20px",
                  fontFamily: "'DM Sans', sans-serif",
                  fontSize: 14,
                  color: "#6d28d9",
                  fontWeight: 500,
                }}
              >
                {t(`landing.gift.${k}`)}
              </span>
            ))}
          </div>

          <div style={fadeUp(giftVisible, 0.25)}>
            <a href={ctaUrl} className="sp-landing-cta" onClick={() => handleCtaClick("gift")}>
              {t("landing.gift.cta")} →
            </a>
          </div>
        </div>
      </section>

      {/* ══════════════════════════════════════════════════════
           FAQ
         ══════════════════════════════════════════════════════ */}
      {/* <section
        style={{ padding: "64px 24px", maxWidth: 600, margin: "0 auto" }}
      >
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: 28,
            color: "#1e1b4b",
            textAlign: "center",
            marginBottom: 32,
          }}
        >
          {t("landing.faq.title")}
        </h2>
        {(["q1", "q2", "q3", "q4"] as const).map((qk) => (
          <FaqItem
            key={qk}
            q={t(`landing.faq.${qk}`)}
            a={t(`landing.faq.${qk.replace("q", "a")}`)}
          />
        ))}
      </section> */}

      {/* FAQ */}
          <section style={{ padding: "64px 24px", maxWidth: 600, margin: "0 auto" }}>
              <h2
                  style={{
                      fontFamily: "'Playfair Display', serif",
                      fontWeight: 700,
                      fontSize: 28,
                      color: "#1e1b4b",
                      textAlign: "center",
                      marginBottom: 32,
                  }}
              >
                  {t("landing.faq.title")}
              </h2>
              {[
                  { q: t("homePage.faq.q1"), a: t("homePage.faq.a1") },
                  { q: t("homePage.faq.q2"), a: t("homePage.faq.a2") },
                  { q: t("homePage.faq.q3"), a: t("homePage.faq.a3") },
                  { q: t("homePage.faq.q4"), a: t("homePage.faq.a4") },
                  { q: t("homePage.faq.q5"), a: t("homePage.faq.a5") },
                  { q: t("homePage.faq.q6"), a: t("homePage.faq.a6") },
                  { q: t("homePage.faq.q7"), a: t("homePage.faq.a7") },
              ].map((item, i) => (
                  <FaqItem key={i} q={item.q} a={item.a} />
              ))}
          </section>

      {/* ══════════════════════════════════════════════════════
           FINAL CTA
         ══════════════════════════════════════════════════════ */}
      <section
        style={{
          padding: "64px 24px 80px",
          background: "linear-gradient(135deg, #7c3aed, #6d28d9)",
          textAlign: "center",
        }}
      >
        <h2
          style={{
            fontFamily: "'Playfair Display', serif",
            fontWeight: 700,
            fontSize: 28,
            color: "#fff",
            marginBottom: 12,
          }}
        >
          {t("landing.finalCta.title")}
        </h2>
        <p
          style={{
            fontFamily: "'DM Sans', sans-serif",
            fontSize: 16,
            color: "rgba(255,255,255,0.8)",
            marginBottom: 32,
            lineHeight: 1.55,
          }}
        >
          {t("landing.finalCta.subtitle")}
        </p>
        <a
          href={ctaUrl}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            background: "#fff",
            color: "#7c3aed",
            border: "none",
            borderRadius: 50,
            padding: "16px 36px",
            fontSize: 17,
            fontWeight: 700,
            fontFamily: "'DM Sans', sans-serif",
            cursor: "pointer",
            textDecoration: "none",
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            transition: "transform 0.2s",
          }}
              >
                  {t("landing.finalCta.cta")}
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="#6d28d9" stroke="none" style={{ verticalAlign: "middle", marginRight: 6 }}>
                      <path d="M12 0C12 0 14 8 16 10C18 12 24 12 24 12C24 12 18 12 16 14C14 16 12 24 12 24C12 24 10 16 8 14C6 12 0 12 0 12C0 12 6 12 8 10C10 8 12 0 12 0Z" />
                  </svg>
              </a>
      </section>
    </div>
  );
};

export default LandingPage;